import { cva, VariantProps } from 'class-variance-authority';
import { ComponentProps } from 'preact/compat';

import { isEmbedded, loadPage } from '@/helpers';
import { cn } from '@/lib/utils';
import { trackMP } from '@/shared/mp';

import HabitatLink, { HabitatLinkProps } from '../HabitatLink';

interface HabitatIconProps extends ComponentProps<'div'> {
  entryPoint?: string;
  habitatId?: string;
  profileImage: string;
  slug?: string;
  zooId?: string;
  zooSlug?: string;
  zooLogo?: string;
  zooName?: string;

  zooIconClassName?: string;
  zooContainerClassName?: string;
  habitatIconClassName?: string;

  habitatLinkProps?: Partial<Omit<HabitatLinkProps, 'habitatId' | 'slug' | 'zooSlug'>>;
}

const habitatIconVariants = cva('preflight preflight-img aspect-square object-cover', {
  variants: {
    variant: {
      default: 'size-[60px] rounded-lg',
      lg: 'size-20 rounded-2xl',
      responsive: 'size-[60px] rounded-md desktop:size-20 desktop:rounded-2xl',
      manual: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const zooIconVariants = cva('preflight preflight-img aspect-square rounded-full object-contain', {
  variants: {
    variant: {
      default: 'size-10',
      lg: 'size-12',
      responsive: 'size-10 desktop:size-12',
      manual: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const zooContainerVariants = cva('absolute rounded-full bg-white shadow-[0_4px_4px_rgba(0,0,0,0.15)]', {
  variants: {
    variant: {
      default: '-bottom-2.5 -right-5',
      lg: '-bottom-3 -right-3',
      responsive: '-bottom-2.5 -right-5 desktop:-bottom-3 desktop:-right-3',
      manual: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const HabitatIcon = ({
  entryPoint,
  profileImage,
  habitatId,
  slug,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  zooId,
  zooSlug,
  zooLogo,
  zooName,
  className,
  title,
  variant,
  habitatIconClassName,
  zooIconClassName,
  zooContainerClassName,
  habitatLinkProps,
  ...props
}: HabitatIconProps & VariantProps<typeof habitatIconVariants>) => {
  const zooContent = zooLogo ? (
    <div className={cn(zooContainerVariants({ variant }), zooContainerClassName)}>
      {zooSlug ? (
        <a
          data-native={isEmbedded() || undefined}
          href={`/h/${zooSlug}`}
          onClick={
            isEmbedded()
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  loadPage(`/h/${zooSlug}`);
                }
              : undefined
          }
          className="preflight preflight-a flex size-full items-center justify-center"
        >
          <img src={zooLogo} alt={zooName ?? 'Zoo logo'} className={cn(zooIconVariants({ variant }), zooIconClassName)} />
        </a>
      ) : (
        <img src={zooLogo} alt={zooName ?? 'Zoo logo'} className={cn(zooIconVariants({ variant }), zooIconClassName)} />
      )}
    </div>
  ) : null;

  const habitatContent = (
    <img
      src={profileImage}
      alt={title ?? slug ?? 'Habitat logo'}
      className={cn(habitatIconVariants({ variant }), habitatIconClassName)}
    />
  );

  const { onClick: onClickProp, ...restHabitatLinkProps } = habitatLinkProps ?? {};

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div className={cn('relative', className)} {...props}>
      {habitatId && zooSlug && slug ? (
        <HabitatLink
          habitatId={habitatId}
          slug={slug}
          zooSlug={zooSlug}
          ppt="sponsor"
          onClick={(e) => {
            trackMP('visit-habitat-link-clicked', {
              habitatId,
              entryPoint,
            });
            onClickProp?.(e);
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...restHabitatLinkProps}
        >
          {habitatContent}
        </HabitatLink>
      ) : (
        habitatContent
      )}
      {zooContent}
    </div>
  );
};

export default HabitatIcon;
